import { useEffect, useMemo, useState } from 'react'
import EventStation from '../../components/captain/EventStation'
import RoleSlotPill from '../../components/captain/RoleSlotPill'
import RoleSelectModal from '../../components/captain/RoleSelectModal'
import EmptyState from '../../components/captain/EmptyState'
import Tabs from '../../components/captain/Tabs'
import type { MarkerState } from '../../components/captain/StationMarker'
import type { CaptainEvent, CaptainRole } from '../../types/captain'

/**
 * A tab appended after the built-in captain tabs. Used by OrganizerView to
 * hang its management surface off the same tab bar (ON-334 follow-up: an
 * organizer is also a captain, so they get the captain tabs plus a "Manage"
 * one rather than a separate view).
 */
export interface ExtraTab {
  id: string
  label: string
  meta?: string
  /** Section eyebrow rendered above the tab's content. */
  eyebrow?: string
  content: React.ReactNode
}

interface Props {
  captainId: string
  upcomingEvents: CaptainEvent[]
  pastEvents: CaptainEvent[]
  /** event_id → signup_id, for cancel / change role RPCs. */
  mySignupIdByEventId: Record<string, string>
  onSignup: (eventId: string, role: CaptainRole) => Promise<string | null>
  onCancel: (signupId: string) => Promise<string | null>
  onChangeRole: (signupId: string, newRole: CaptainRole) => Promise<string | null>
  extraTabs?: ExtraTab[]
  /**
   * ON-426: event id from a `?event_id=` call-out link that is present in
   * `upcomingEvents`. When set, the matching station is highlighted, scrolled
   * into view, and RoleSelectModal opens for it once.
   */
  autoOpenEventId?: string | null
}

type Tab = string

// ON-334: participant-facing — never returns 'full'. Roles are a minimum
// target with unlimited overflow, so there is no cap/closed state to signal
// here; a station is either awaiting its first captain or has some interest.
function markerStateFor(event: CaptainEvent, isPast?: boolean): MarkerState {
  if (isPast) return 'past'
  const totalFilled = event.slots.reduce((a, s) => a + s.filledBy.length, 0)
  return totalFilled === 0 ? 'open' : 'partial'
}

function signupSummary(totalFilled: number): string {
  if (totalFilled === 0) return 'No captains signed up yet — be the first'
  return `${totalFilled} captain${totalFilled === 1 ? '' : 's'} signed up — join them`
}

/** DOM id for a station card, used as the scroll target for deep links. */
export function stationDomId(eventId: string): string {
  return `cp-station-${eventId}`
}

function captainRoleInEvent(
  event: CaptainEvent,
  captainId: string
): CaptainRole | undefined {
  return event.slots.find((s) => s.filledBy.includes(captainId))?.role
}

export default function CaptainView({
  captainId,
  upcomingEvents,
  pastEvents,
  mySignupIdByEventId,
  onSignup,
  onCancel,
  onChangeRole,
  extraTabs = [],
  autoOpenEventId = null,
}: Props) {
  const [tab, setTab] = useState<Tab>('upcoming')
  const activeExtraTab = extraTabs.find((t) => t.id === tab)
  const [modalEventId, setModalEventId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [pendingEventId, setPendingEventId] = useState<string | null>(null)
  const [highlightEventId, setHighlightEventId] = useState<string | null>(null)

  // Deep-link auto-open: fires when the portal hands us a resolved event id.
  // Only events actually in the list get here (CaptainPortal filters), so the
  // modal lookup below always resolves.
  useEffect(() => {
    if (!autoOpenEventId) return
    setTab('upcoming')
    setHighlightEventId(autoOpenEventId)
    setModalEventId(autoOpenEventId)
    // Bring the station into view behind the modal so the context is there
    // when the volunteer confirms or dismisses.
    if (typeof document !== 'undefined') {
      document
        .getElementById(stationDomId(autoOpenEventId))
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [autoOpenEventId])

  const mySignups = useMemo(
    () => upcomingEvents.filter((e) => captainRoleInEvent(e, captainId)),
    [upcomingEvents, captainId]
  )

  const myPast = useMemo(
    () => pastEvents.filter((e) => captainRoleInEvent(e, captainId)),
    [pastEvents, captainId]
  )

  async function handleSignUp(eventId: string, role: CaptainRole) {
    setError(null)
    setPendingEventId(eventId)
    const existingSignupId = mySignupIdByEventId[eventId]
    const err = existingSignupId
      ? await onChangeRole(existingSignupId, role)
      : await onSignup(eventId, role)
    setPendingEventId(null)
    if (err) {
      setError(err)
    } else {
      setModalEventId(null)
    }
  }

  async function handleCancelClick(eventId: string) {
    setError(null)
    const signupId = mySignupIdByEventId[eventId]
    if (!signupId) {
      setError('Could not find your sign-up. Please refresh and try again.')
      return
    }
    setPendingEventId(eventId)
    const err = await onCancel(signupId)
    setPendingEventId(null)
    if (err) setError(err)
  }

  const modalEvent = modalEventId
    ? upcomingEvents.find((e) => e.id === modalEventId)
    : null

  return (
    <>
      <Tabs
        tabs={[
          {
            id: 'upcoming',
            label: 'Upcoming events',
            meta: String(upcomingEvents.length).padStart(2, '0'),
          },
          {
            id: 'mine',
            label: 'My sign-ups',
            meta: String(mySignups.length).padStart(2, '0'),
          },
          ...extraTabs.map((t) => ({ id: t.id, label: t.label, meta: t.meta })),
        ]}
        active={tab}
        onChange={setTab}
      />

      <SectionEyebrow>
        {activeExtraTab
          ? activeExtraTab.eyebrow ?? ''
          : tab === 'upcoming'
            ? 'Stations along the next two weeks'
            : 'Where you are committed'}
      </SectionEyebrow>

      {error && !activeExtraTab && (
        <ErrorBanner message={error} onDismiss={() => setError(null)} />
      )}

      {activeExtraTab?.content}

      {tab === 'upcoming' && upcomingEvents.length === 0 && (
        <EmptyState
          eyebrow="All stations crewed"
          title="No events currently need captains."
          body="Check back soon — your club organizer flags events when they need volunteers."
        />
      )}

      {tab === 'upcoming' && upcomingEvents.length > 0 && (
        <div
          className="cp-cascade cp-layout"
          style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
        >
          {upcomingEvents.map((event, idx) => {
            const myRole = captainRoleInEvent(event, captainId)
            const state = markerStateFor(event)
            const isPending = pendingEventId === event.id
            const totalFilled = event.slots.reduce((a, s) => a + s.filledBy.length, 0)
            return (
              <EventStation
                key={event.id}
                event={event}
                markerState={state}
                mine={!!myRole}
                isFirst={idx === 0}
                isLast={idx === upcomingEvents.length - 1}
                domId={stationDomId(event.id)}
                highlighted={highlightEventId === event.id}
              >
                {/* ON-334: open-ended — count only, never a cap/full/closed state. */}
                <div
                  style={{
                    fontFamily: "'Lora', Georgia, serif",
                    fontSize: 13,
                    color: 'rgba(198,196,185,0.7)',
                    marginBottom: 12,
                  }}
                >
                  {signupSummary(totalFilled)}
                </div>

                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    flexWrap: 'wrap',
                    marginBottom: 22,
                  }}
                >
                  {event.slots.map((slot) => {
                    const filled = slot.filledBy.length
                    const variant = myRole === slot.role ? 'mine' : 'open'
                    return (
                      <RoleSlotPill
                        key={slot.role}
                        role={slot.role}
                        variant={variant}
                        meta={filled > 0 ? `${filled} signed up` : undefined}
                      />
                    )
                  })}
                </div>

                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {myRole ? (
                    <>
                      <button
                        className="cp-btn-ghost"
                        onClick={() => setModalEventId(event.id)}
                        disabled={isPending}
                      >
                        Change role
                      </button>
                      <button
                        className="cp-btn-danger"
                        onClick={() => handleCancelClick(event.id)}
                        disabled={isPending}
                      >
                        {isPending ? 'Working…' : 'Cancel sign-up'}
                      </button>
                    </>
                  ) : (
                    <button
                      className="cp-btn-primary"
                      onClick={() => setModalEventId(event.id)}
                      disabled={isPending}
                    >
                      Volunteer for a role
                    </button>
                  )}
                </div>
              </EventStation>
            )
          })}
        </div>
      )}

      {tab === 'mine' && (
        <>
          {mySignups.length === 0 && myPast.length === 0 && (
            <EmptyState
              eyebrow="No stations yet"
              title="You haven't signed up for any events yet."
              body="When you volunteer for a role, your events will appear here so you can manage them in one place."
            >
              <button
                onClick={() => setTab('upcoming')}
                className="cp-btn-ghost"
              >
                See what needs captains
              </button>
            </EmptyState>
          )}

          {mySignups.length > 0 && (
            <div
              className="cp-cascade cp-layout"
              style={{ display: 'flex', flexDirection: 'column', gap: 0, marginBottom: 40 }}
            >
              {mySignups.map((event, idx) => {
                const myRole = captainRoleInEvent(event, captainId)!
                const isPending = pendingEventId === event.id
                return (
                  <EventStation
                    key={event.id}
                    event={event}
                    markerState="partial"
                    mine
                    isFirst={idx === 0}
                    isLast={idx === mySignups.length - 1}
                  >
                    <div style={{ marginBottom: 22 }}>
                      <RoleSlotPill role={myRole} variant="mine" />
                    </div>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                      <button
                        className="cp-btn-ghost"
                        onClick={() => setModalEventId(event.id)}
                        disabled={isPending}
                      >
                        Change role
                      </button>
                      <button
                        className="cp-btn-danger"
                        onClick={() => handleCancelClick(event.id)}
                        disabled={isPending}
                      >
                        {isPending ? 'Working…' : 'Cancel sign-up'}
                      </button>
                    </div>
                  </EventStation>
                )
              })}
            </div>
          )}

          {myPast.length > 0 && (
            <>
              <SectionEyebrow muted>Stations you've crewed</SectionEyebrow>
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
              >
                {myPast.map((event, idx) => {
                  const myRole = captainRoleInEvent(event, captainId)!
                  return (
                    <EventStation
                      key={event.id}
                      event={event}
                      markerState="past"
                      mine
                      isFirst={idx === 0}
                      isLast={idx === myPast.length - 1}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 12,
                          flexWrap: 'wrap',
                        }}
                      >
                        <RoleSlotPill role={myRole} variant="past" />
                        <span
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 6,
                            fontFamily:
                              "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
                            fontSize: 10,
                            color: 'rgba(198,196,185,0.6)',
                            textTransform: 'uppercase',
                            letterSpacing: '0.14em',
                          }}
                        >
                          <span
                            style={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              background: 'rgba(198,196,185,0.4)',
                            }}
                          />
                          Completed
                        </span>
                      </div>
                    </EventStation>
                  )
                })}
              </div>
            </>
          )}
        </>
      )}

      {modalEvent && (
        <RoleSelectModal
          event={modalEvent}
          currentRole={captainRoleInEvent(modalEvent, captainId)}
          onConfirm={(role) => handleSignUp(modalEvent.id, role)}
          onCancel={() => setModalEventId(null)}
        />
      )}
    </>
  )
}

function ErrorBanner({
  message,
  onDismiss,
}: {
  message: string
  onDismiss: () => void
}) {
  return (
    <div
      role="alert"
      style={{
        marginBottom: 24,
        padding: '12px 16px',
        borderRadius: 8,
        background: 'rgba(239,96,106,0.08)',
        border: '0.5px solid rgba(239,96,106,0.4)',
        color: '#EF606A',
        fontFamily: "'Lora', Georgia, serif",
        fontSize: 14,
        lineHeight: 1.5,
        display: 'flex',
        gap: 12,
        alignItems: 'center',
        justifyContent: 'space-between',
      }}
    >
      <span>{message}</span>
      <button
        onClick={onDismiss}
        style={{
          background: 'transparent',
          border: 'none',
          color: 'rgba(239,96,106,0.85)',
          cursor: 'pointer',
          fontSize: 14,
          padding: 4,
        }}
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  )
}

function SectionEyebrow({
  children,
  muted,
}: {
  children: React.ReactNode
  muted?: boolean
}) {
  return (
    <div
      style={{
        fontFamily: "'Helvetica Neue', 'HelveticaNeue', Helvetica, 'Inter', Arial, sans-serif",
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.14em',
        color: muted ? 'rgba(198,196,185,0.4)' : 'rgba(198,196,185,0.6)',
        marginBottom: 28,
        marginTop: muted ? 24 : 0,
      }}
    >
      {children}
    </div>
  )
}
